import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, ChevronLeft } from 'lucide-react'
import ConfirmDialog from './ConfirmDialog'

// Modal central em passos (wizard next/next/done) para os cadastros.
// steps: [{ title, valid(form) => bool, render(form, setForm) => JSX }]
//   ou uma função (form) => [...] para wizards com passos dinâmicos.
// onFinish(form, setForm) é chamado no Concluir; deve lançar erro para manter o modal
// aberto. O setForm serve para o wizard gravar o que já subiu (ex.: id de imagem), para
// uma tentativa nova não reenviar tudo e deixar órfãos.
export default function WizardModal({ open, onClose, title, icon, steps, initial, onFinish, finishLabel }) {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [form, setForm] = useState(initial || {})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [askDiscard, setAskDiscard] = useState(false)
  const baselineRef = useRef(JSON.stringify(initial || {}))

  useEffect(() => {
    if (open) {
      setStep(0); setForm(initial || {}); setBusy(false); setError(null); setAskDiscard(false)
      baselineRef.current = JSON.stringify(initial || {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Fechar um cadastro de 149 itens por um toque no scrim ou no Esc perdia tudo sem
  // perguntar: só fecha direto quando nada foi alterado.
  const dirty = () => JSON.stringify(form) !== baselineRef.current
  const requestClose = () => {
    if (busy) return
    if (dirty()) { setAskDiscard(true); return }
    onClose?.()
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') requestClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, form, busy])

  if (!open) return null

  const stepList = typeof steps === 'function' ? steps(form) : steps
  const idx = Math.min(step, stepList.length - 1)
  const current = stepList[idx]
  const canNext = !current.valid || current.valid(form)
  const isLast = idx === stepList.length - 1

  const next = async () => {
    setError(null)
    if (!isLast) { setStep(idx + 1); return }
    setBusy(true)
    try {
      await onFinish(form, setForm)
      onClose?.()
    } catch (err) {
      setError(err.message)
    } finally { setBusy(false) }
  }

  const back = () => { setError(null); setStep(Math.max(0, idx - 1)) }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={requestClose} />
      <div role="dialog" aria-modal="true"
        className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            {icon}
            <div className="min-w-0">
              <h2 className="font-semibold text-slate-800 dark:text-slate-100 truncate leading-tight">{title}</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t('admin:wizard.step_of', { current: idx + 1, total: stepList.length, title: current.title })}
              </p>
            </div>
          </div>
          <button onClick={requestClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 shrink-0" aria-label={t('common:actions.close')}>
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-1.5 px-5 pt-3">
          {stepList.map((st, i) => (
            <div key={i} title={st.title}
              className={`h-1.5 flex-1 rounded-full transition-colors ${i < idx ? 'bg-brand/60' : i === idx ? 'bg-brand' : 'bg-slate-200 dark:bg-slate-700'}`} />
          ))}
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-4">
          {current.render(form, setForm)}
          {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button type="button" onClick={back} disabled={idx === 0}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-0 disabled:pointer-events-none">
            <ChevronLeft size={15} /> {t('common:actions.back')}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={requestClose}
              className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              {t('common:actions.cancel')}
            </button>
            <button type="button" onClick={next} disabled={!canNext || busy}
              className="text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500">
              {busy ? t('admin:actions.saving') : isLast ? (finishLabel ?? t('common:actions.finish')) : t('admin:wizard.next')}
            </button>
          </div>
        </div>
      </div>

      {askDiscard && (
        <ConfirmDialog
          title={t('admin:wizard.discard_title')}
          message={t('admin:wizard.discard_message')}
          confirmLabel={t('admin:wizard.discard_confirm')}
          danger
          onConfirm={() => onClose?.()}
          onClose={() => setAskDiscard(false)}
        />
      )}
    </div>
  )
}

// Estilos de input compartilhados pelos wizards (mesmo idioma visual dos Admin*).
export const inputCls = 'mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500'
export const selectCls = 'mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100'
export const labelCls = 'text-xs font-medium text-slate-600 dark:text-slate-300'
